import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const isAuthenticated = !!user;

  const fetchUser = useCallback(async () => {
    try {
      const response = await api.get('/api/auth/me');
      const meData = response.data;
      setUser(meData.user || meData);
      return meData.user || meData;
    } catch (error) {
      console.error('Error fetching user:', error);
      setUser(null);
      localStorage.removeItem('token');
      return null;
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchUser().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [fetchUser]);

  const login = async (email, password) => {
    try {
      const response = await api.post('/api/auth/login', { email, password });
      const { access_token, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(userData);
      // Fetch full user profile
      try {
        const meResponse = await api.get('/api/auth/me');
        const meData = meResponse.data;
        // /me returns {user, structure, app_subscriptions} — extract user
        setUser(meData.user || meData);
      } catch (meError) {
        console.error('Error fetching user details after login:', meError);
      }
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Erreur de connexion',
      };
    }
  };

  const register = async (data) => {
    try {
      const response = await api.post('/api/auth/register', data);
      const { access_token, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(userData);
      try {
        const meResponse = await api.get('/api/auth/me');
        const meData = meResponse.data;
        setUser(meData.user || meData);
      } catch (meError) {
        console.error('Error fetching user details after register:', meError);
      }
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || "Erreur lors de l'inscription",
      };
    }
  };

  const logout = async () => {
    try {
      await api.post('/api/auth/logout');
    } catch (error) {
      // Ignore logout errors
    }
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    window.location.href = '/login';
  };

  const refreshToken = async () => {
    try {
      const response = await api.post('/api/auth/refresh');
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      return access_token;
    } catch (error) {
      logout();
      return null;
    }
  };

  const hasAppAccess = (appCode) => {
    if (!user || !user.app_roles) return false;
    return !!user.app_roles[appCode];
  };

  const hasNexusRole = (role) => {
    if (!user) return false;
    if (Array.isArray(role)) {
      return role.includes(user.nexus_role);
    }
    return user.nexus_role === role;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated,
        login,
        register,
        logout,
        fetchUser,
        refreshToken,
        hasAppAccess,
        hasNexusRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
